import React, { useState } from 'react';
import { X, Upload, FileText, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { sendJobApplication } from '../../services/emailService';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const JobApplicationModal = ({ isOpen, onClose, jobTitle }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        coverLetter: ''
    });
    const [applicationForm, setApplicationForm] = useState(null);
    const [cvFile, setCvFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (type === 'application') {
            setApplicationForm(file);
        } else {
            setCvFile(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Create application message for admin dashboard
            const applicationMessage = {
                id: Date.now().toString(),
                type: 'application',
                name: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                productTitle: `Job Application: ${jobTitle}`,
                message: formData.coverLetter || 'No cover letter provided',
                position: jobTitle,
                read: false,
                created_at: new Date().toISOString()
            };

            // Save to Firestore (primary)
            try {
                const docRef = await addDoc(collection(db, 'messages'), applicationMessage);
                console.log('✅ Job application saved to Firestore with ID:', docRef.id);
            } catch (firestoreError) {
                console.error('❌ Firestore write failed:', firestoreError.message);
            }

            // Save to localStorage (backup)
            const MESSAGES_KEY = 'siteMessages';
            const existingMessages = JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');
            existingMessages.unshift(applicationMessage);
            localStorage.setItem(MESSAGES_KEY, JSON.stringify(existingMessages));

            // Dispatch event for real-time notification in admin dashboard
            window.dispatchEvent(new CustomEvent('newMessage', { detail: applicationMessage }));

            // Send email to info@pridepath.lk
            try {
                await sendJobApplication({
                    ...formData,
                    position: jobTitle
                }, cvFile);
                console.log('✅ Job application email sent to info@pridepath.lk');
            } catch (emailError) {
                console.warn('⚠️ Email sending failed:', emailError);
                // Don't fail the submission if email fails
            }

            setSubmitStatus('success');

            // Reset form after successful submission
            setTimeout(() => {
                setFormData({ fullName: '', email: '', phone: '', coverLetter: '' });
                setApplicationForm(null);
                setCvFile(null);
                onClose();
                setSubmitStatus(null);
            }, 2000);

        } catch (error) {
            console.error('Error submitting application:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-fadeIn">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#2A9D8F] to-[#004a8b] text-white p-6">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <X size={24} />
                    </button>
                    <h2 className="text-2xl font-bold">Apply for {jobTitle}</h2>
                    <p className="text-white/80 mt-1">Fill out the form below to submit your application</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
                    {/* Personal Information */}
                    <div className="space-y-4 mb-6">
                        <h3 className="font-semibold text-gray-900 border-b pb-2">Personal Information</h3>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                            <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition-all"
                                placeholder="Enter your full name"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition-all"
                                    placeholder="your@email.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition-all"
                                    placeholder="+94 XX XXX XXXX"
                                />
                            </div>
                        </div>
                    </div>

                    {/* File Uploads */}
                    <div className="space-y-4 mb-6">
                        <h3 className="font-semibold text-gray-900 border-b pb-2">Documents</h3>

                        {/* Application Form Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Application Form</label>
                            <div className="relative">
                                <input
                                    type="file"
                                    id="applicationForm"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => handleFileChange(e, 'application')}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="applicationForm"
                                    className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2A9D8F] hover:bg-[#2A9D8F]/5 transition-all"
                                >
                                    <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center">
                                        <Upload className="text-[#2A9D8F]" size={20} />
                                    </div>
                                    <div className="flex-1">
                                        {applicationForm ? (
                                            <div className="flex items-center gap-2">
                                                <FileText className="text-[#2A9D8F]" size={16} />
                                                <span className="text-gray-900 font-medium">{applicationForm.name}</span>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-gray-900 font-medium">Upload Application Form</p>
                                                <p className="text-gray-500 text-sm">PDF, DOC, or DOCX (Max 5MB)</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* CV Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Curriculum Vitae (CV) *</label>
                            <div className="relative">
                                <input
                                    type="file"
                                    id="cvFile"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => handleFileChange(e, 'cv')}
                                    required
                                    className="hidden"
                                />
                                <label
                                    htmlFor="cvFile"
                                    className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2A9D8F] hover:bg-[#2A9D8F]/5 transition-all"
                                >
                                    <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center">
                                        <Upload className="text-[#2A9D8F]" size={20} />
                                    </div>
                                    <div className="flex-1">
                                        {cvFile ? (
                                            <div className="flex items-center gap-2">
                                                <FileText className="text-[#2A9D8F]" size={16} />
                                                <span className="text-gray-900 font-medium">{cvFile.name}</span>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-gray-900 font-medium">Upload your CV</p>
                                                <p className="text-gray-500 text-sm">PDF, DOC, or DOCX (Max 5MB)</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Cover Letter */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter (Optional)</label>
                        <textarea
                            name="coverLetter"
                            value={formData.coverLetter}
                            onChange={handleInputChange}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition-all resize-none"
                            placeholder="Tell us why you're interested in this position..."
                        />
                    </div>

                    {/* Status Messages */}
                    {submitStatus === 'success' && (
                        <div className="flex items-center gap-2 p-4 bg-green-50 text-green-700 rounded-lg mb-4">
                            <CheckCircle size={20} />
                            <span>Your application has been submitted successfully!</span>
                        </div>
                    )}

                    {submitStatus === 'error' && (
                        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg mb-4">
                            <AlertCircle size={20} />
                            <span>There was an error submitting your application. Please try again.</span>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2A9D8F] to-[#004a8b] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send size={18} />
                                    Submit Application
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Animation Styles */}
                <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.95) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style>
            </div>
        </div>
    );
};

export default JobApplicationModal;
